/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import {
  FaUser,
  FaCalendarAlt,
  FaDollarSign,
  FaCommentDots,
  FaUserShield,
  FaInfoCircle,
  FaFileAlt,
  FaDownload,
  FaTrash,
} from "react-icons/fa";
import { Loader } from "lucide-react";
import formatCurrency from "util/currency";
import { dateFormate } from "util/Util";
import reimbursementManager from "services/Reimbursement/reimbursementManager";
import { useToaster } from "contexts/toasterContext";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
import MediaPreview, {
  useMediaPreview,
  MediaThumbnail,
  MediaItem,
  triggerDownload,
  resolveDownloadName,
} from "components/MediaPreview";

const InfoView: React.FC<any> = (props) => {
  const {
    id,
    type,
    description,
    comment,
    status,
    username,
    admin,
    submitDate,
    spentDate,
    submitAmount,
    approveAmount,
    differenceAmount,
    unit,
    data,
    dataType,
    dataOriginalName,
    files,
  } = props;

  const { addToast } = useToaster();
  const [downloading, setDownloading] = useState(false);
  // Files removed during this view session (hidden after a successful delete).
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Attachment awaiting delete confirmation (drives the confirm popup).
  const [pendingDelete, setPendingDelete] = useState<{ fileId: number; item: MediaItem } | null>(null);

  // Attachments are only removable while the reimbursement is still INITIATED.
  const canDeleteFiles = status === "INITIATED";

  // Build the attachment list, keeping each file's id (needed for delete). New
  // records return a `files` array (url + originalName); older records used a
  // single/array `data` field — support both so the preview always renders.
  // `dataType` is the record-level MIME hint.
  const attachments: { fileId: number | null; item: MediaItem }[] = (() => {
    if (Array.isArray(files) && files.length) {
      return files
        .filter((f: any) => f && typeof f.url === "string" && f.url.trim())
        .map((f: any) => ({
          fileId: typeof f.id === "number" ? f.id : null,
          item: { url: f.url, name: f.originalName ?? undefined, mimeType: dataType ?? undefined },
        }));
    }
    const urls = Array.isArray(data) ? data : data ? [data] : [];
    return urls
      .filter((u: any): u is string => typeof u === "string" && !!u.trim())
      .map((u: string) => ({
        fileId: null,
        item: { url: u, name: dataOriginalName ?? undefined, mimeType: dataType ?? undefined },
      }));
  })().filter((a) => a.fileId == null || !removedIds.includes(a.fileId));

  const mediaItems: MediaItem[] = attachments.map((a) => a.item);

  const handleDownload = async (item: MediaItem) => {
    if (downloading) return;
    setDownloading(true);
    try {
      await triggerDownload(item);
    } finally {
      setDownloading(false);
    }
  };

  // Confirmed delete of a single attachment; hides it from the list on success.
  const handleDeleteFile = async (fileId: number) => {
    if (deletingId != null) return;
    setDeletingId(fileId);
    try {
      await reimbursementManager.deleteReimbursementFile(id, fileId);
      setRemovedIds((prev) => [...prev, fileId]);
      addToast("Attachment removed successfully", "success");
    } catch (err: any) {
      addToast(
        err?.response?.data?.message || err?.response?.data || "Failed to remove attachment",
        "error",
      );
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
    }
  };

  const { previewProps, open: openPreview } = useMediaPreview(mediaItems);

  const statusColors: any = {
    INITIATED: "bg-primary/10 text-primary border-primary/20",
    APPROVED: "bg-success/10 text-success border-success/20",
    REJECTED: "bg-error/10 text-error border-error/20",
    PAID: "bg-secondary/10 text-secondary border-secondary/20",
    PENDING: "bg-warning/10 text-warning border-warning/20",
  };

  return (
    <>
      <div className="mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-800">
        <div className="md:flex">

          {/* ── Left panel: file preview ── */}
          <div className="bg-slate-50 dark:bg-slate-800/50 p-6 md:w-1/3 border-r border-slate-100 dark:border-slate-800">
            {/* Single column — no items-center so every child stretches to the same full width */}
            <div className="flex flex-col gap-3">
              {mediaItems.length > 0 ? (
                <>
                  {mediaItems.length > 1 && (
                    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                      Attachments ({mediaItems.length})
                    </p>
                  )}

                  {/* One card per attachment — thumbnail + preview/download/delete */}
                  <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
                    {attachments.map(({ fileId, item }, index) => (
                      <div
                        key={fileId ?? index}
                        className="flex flex-col gap-2 rounded-xl border border-slate-200 dark:border-slate-700 p-2 bg-white dark:bg-gray-800"
                      >
                        <div className="relative h-44 rounded-lg overflow-hidden">
                          <MediaThumbnail
                            item={item}
                            onClick={() => openPreview(index)}
                            className="!w-full !h-full !rounded-lg"
                          />

                          {/* Delete — overlaid on the thumbnail so it never
                              overflows the card. Shown only while INITIATED and
                              when more than one file exists (keep at least one
                              attachment as evidence). */}
                          {canDeleteFiles && attachments.length > 1 && fileId != null && (
                            <button
                              type="button"
                              onClick={() => setPendingDelete({ fileId, item })}
                              disabled={deletingId != null}
                              title="Remove attachment"
                              className="absolute top-2 right-2 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white/90 dark:bg-gray-900/80 text-red-500 shadow-sm ring-1 ring-red-200 dark:ring-red-800 hover:bg-red-50 dark:hover:bg-red-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {deletingId === fileId
                                ? <Loader size={13} className="animate-spin" />
                                : <FaTrash size={12} />}
                            </button>
                          )}
                        </div>

                        {item.name && (
                          <p
                            className="text-xs text-slate-500 dark:text-slate-400 text-center truncate px-1"
                            title={item.name}
                          >
                            {item.name}
                          </p>
                        )}

                        {/* Buttons — Preview + Download split the width evenly. */}
                        <div className="flex gap-2 w-full">
                          <button
                            type="button"
                            onClick={() => openPreview(index)}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Preview
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDownload(item)}
                            disabled={downloading}
                            title={`Download ${resolveDownloadName(item)}`}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-primary text-xs font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity"
                          >
                            {downloading
                              ? <Loader size={13} className="animate-spin" />
                              : <FaDownload size={12} />}
                            {downloading ? "Downloading…" : "Download"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                // No attachment uploaded
                <div className="flex flex-col items-center justify-center h-52 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800">
                  <FaFileAlt className="text-slate-300 dark:text-slate-600 mb-3" size={40} />
                  <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">No attachment</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Right panel: detail fields ── */}
          <div className="p-8 flex-grow w-full md:w-2/3 space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div>
                <span className="text-sm font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 block mb-1">
                  Reimbursement Type
                </span>
                <span className="text-2xl font-bold text-primary">{type}</span>
              </div>
              <div
                className={`rounded-full px-4 py-1.5 text-xs font-bold tracking-widest uppercase border ${statusColors[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}
              >
                {status}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary mt-1">
                  <FaUser size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                    Submitted by
                  </p>
                  <p className="text-slate-900 dark:text-white font-bold">{username}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary mt-1">
                  <FaCalendarAlt size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                    Submit Date
                  </p>
                  <p className="text-slate-900 dark:text-white font-bold">
                    {dateFormate(submitDate)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary mt-1">
                  <FaCalendarAlt size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                    Spent Date
                  </p>
                  <p className="text-slate-900 dark:text-white font-bold">
                    {dateFormate(spentDate)}
                  </p>
                </div>
              </div>

              {comment && (
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-warning/10 text-warning mt-1">
                    <FaCommentDots size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                      Admin Comment
                    </p>
                    <p className="text-slate-700 dark:text-slate-300 font-medium italic">"{comment}"</p>
                  </div>
                </div>
              )}

              {admin && (
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-secondary/10 text-secondary mt-1">
                    <FaUserShield size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                      Processed by
                    </p>
                    <p className="text-slate-900 dark:text-white font-bold">{admin}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary mt-1">
                  <FaDollarSign size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                    Submit Amount
                  </p>
                  <p className="text-slate-900 dark:text-white font-bold">
                    {formatCurrency(submitAmount, unit)}
                  </p>
                </div>
              </div>

              {approveAmount !== null && (
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-success/10 text-success mt-1">
                    <FaDollarSign size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                      Approved Amount
                    </p>
                    <p className="text-success font-bold">
                      {formatCurrency(approveAmount, unit)}
                    </p>
                  </div>
                </div>
              )}

              {differenceAmount !== null && (
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-error/10 text-error mt-1">
                    <FaInfoCircle size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                      Difference Amount
                    </p>
                    <p className="text-error font-bold">
                      {formatCurrency(differenceAmount, unit)}
                    </p>
                  </div>
                </div>
              )}

              <div className="col-span-1 sm:col-span-2 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    <FaFileAlt size={18} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                      Description
                    </p>
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed italic">
                      {description || "No description provided."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-screen lightbox — handles image zoom, PDF iframe, video player */}
      <MediaPreview {...previewProps} />

      {/* Delete-attachment confirmation — reuses the shared DeletePopup, with a
          thumbnail + file name preview of the attachment being removed. */}
      {pendingDelete && (
        <GenericModal
          isOpen={!!pendingDelete}
          onClose={() => setPendingDelete(null)}
          header="Delete Attachment"
          size="sm"
        >
          <DeletePopup
            handleDelete={(fileId) => handleDeleteFile(fileId)}
            onClose={() => setPendingDelete(null)}
            selectedRow={{ id: pendingDelete.fileId }}
            message="Are you sure you want to delete this attachment?"
          >
            <div className="flex flex-col items-center gap-2">
              <div className="w-24 h-24 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                <MediaThumbnail
                  item={pendingDelete.item}
                  onClick={() => { /* preview only in the main view */ }}
                  className="!w-full !h-full !rounded-lg"
                />
              </div>
              {pendingDelete.item.name && (
                <p
                  className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate max-w-[240px]"
                  title={pendingDelete.item.name}
                >
                  {pendingDelete.item.name}
                </p>
              )}
            </div>
          </DeletePopup>
        </GenericModal>
      )}
    </>
  );
};

export default InfoView;
